/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 7,
  pageTotal: 0,
};

function CouponBrand({ add }) {
  const mod = 'brand';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: '#', align: 'center' },
    { id: 'tb.name', label: 'ยี่ห้อ', align: 'left' },
    { id: 'tb.name_en', label: 'ยี่ห้อ EN', align: 'left' },
    { id: 'add', label: '', align: 'center', sort: false },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('OPTIONS', `/${mod}/search`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 7 })}>
        {t('element.show_record', { num: 7 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t('element.show_record', { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t('element.show_record', { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const addCallback = (item) => {
    add({ id: item.id });
  };

  const btnAdd = (item) => (
    <IconButton color='error' onClick={() => addCallback(item)}>
      <Icon>add_circle</Icon>
    </IconButton>
  );

  const renderSearch = () => (
    <MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, page: 1 });
            }}
          >
            <MDInput
              name='searchbrandform'
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                inputRef: txtSearch,
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );

  const renderList = () => (
    <MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderSearch()}
        </Grid>
        <Grid item xs={12}>
          <TableContainer sx={{ boxShadow: 0, minHeight: 400 }}>
            <Table aria-label='coupon-brand-table'>
              <TableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleSort}
                data={initHeader}
              />
              <TableBody>
                {record.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align='center'>{row.id}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.name_en}</TableCell>
                    <TableCell align='center'>{btnAdd(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
            <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
              {t('element.show_record', { num: find.perPage })}&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderShow}
            <Pagination
              count={pageTotal}
              variant='outlined'
              color='secondary'
              showFirstButton
              showLastButton
              page={pageNo}
              onChange={(e, p) => view({ ...find, page: p })}
            />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );

  const renderModal = () => (
    <Dialog
      open={modalAdd}
      maxWidth='xl'
      keepMounted
      aria-describedby='dialog-coupon-brand'
      fullWidth
      onClose={() => setModalAdd(false)}
    >
      <DialogTitle>
        เพิ่มสินค้าตามยี่ห้อ
        <IconButton
          aria-label='close'
          onClick={() => setModalAdd(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{renderList()}</DialogContent>
    </Dialog>
  );

  return (
    <div>
      <MDButton
        color='warning'
        onClick={() => {
          setModalAdd(true);
          view(find);
          setTimeout(() => {
            if (txtSearch.current) txtSearch.current.focus();
          }, 500);
        }}
      >
        <Icon>add_circle_outline</Icon>&nbsp;เพิ่มตามยี่ห้อ
      </MDButton>

      {renderModal()}
      {renderLoading()}
    </div>
  );
}

CouponBrand.propTypes = {
  add: PropTypes.func.isRequired,
};

export default CouponBrand;
